import { Link, useLocation } from 'react-router-dom';
import { Home as HomeIcon, Layers, Plus, ListChecks, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppShell } from './AppShellContext';

/**
 * Mobile bottom navigation. Five entries chosen for *workflow*
 * frequency, not schema enumeration:
 *
 *   Home · Domains · Capture (action) · Checklists · Search
 *
 * The center cell is *not* a route — it's a primary action that
 * opens the global Capture sheet from anywhere. Tapping the rest
 * navigates.
 *
 * Hidden on `md:` and up; the desktop sidebar takes over.
 */
export default function BottomNav() {
  const { pathname } = useLocation();
  const { openCapture, openSearch } = useAppShell();

  const isActive = (path: string, end = false) =>
    end ? pathname === path : pathname === path || pathname.startsWith(`${path}/`);

  return (
    <nav
      aria-label="Primary"
      className={cn(
        'fixed inset-x-0 bottom-0 z-40 md:hidden',
        // Top hairline rule and a slightly elevated cream background
        // so it reads as a tray, not a banner.
        'border-t border-border bg-background/95 backdrop-blur',
        // Safe-area inset so iPhone home-indicator doesn't overlap
        // the tap targets.
        'pb-[env(safe-area-inset-bottom)]',
      )}
    >
      <ul className="grid grid-cols-5">
        <NavCell to="/" label="Home" Icon={HomeIcon} active={isActive('/', true)} />
        <NavCell to="/domains" label="Domains" Icon={Layers} active={isActive('/domains')} />
        <li className="flex items-center justify-center pt-2">
          <button
            type="button"
            onClick={openCapture}
            aria-label="Capture"
            className={cn(
              // Raised circular primary button — the single
              // most-prominent affordance on a phone.
              'flex h-14 w-14 -translate-y-3 items-center justify-center rounded-full',
              'bg-primary text-primary-foreground shadow-lg',
              'ring-4 ring-background',
              'transition-transform active:scale-95',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            )}
          >
            <Plus className="h-6 w-6" aria-hidden />
          </button>
        </li>
        <NavCell
          to="/checklists"
          label="Checklists"
          Icon={ListChecks}
          active={isActive('/checklists')}
        />
        <li className="flex items-stretch">
          <button
            type="button"
            onClick={openSearch}
            aria-label="Search"
            className={cn(
              'group flex flex-1 flex-col items-center justify-center gap-0.5 py-2.5',
              'text-[11px] font-medium text-muted-foreground',
              'transition-colors hover:text-foreground',
            )}
          >
            <Search className="h-5 w-5" aria-hidden />
            <span>Search</span>
          </button>
        </li>
      </ul>
    </nav>
  );
}

function NavCell({
  to,
  label,
  Icon,
  active,
}: {
  to: string;
  label: string;
  Icon: typeof HomeIcon;
  active: boolean;
}) {
  return (
    <li className="flex items-stretch">
      <Link
        to={to}
        className={cn(
          'group relative flex flex-1 flex-col items-center justify-center gap-0.5 py-2.5',
          'text-[11px] font-medium',
          active ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
        )}
      >
        {/* Active state: thin accent rule above the icon, evoking a
            page-marker tab in a binder. */}
        <span
          aria-hidden
          className={cn(
            'absolute inset-x-6 top-0 h-0.5 rounded-b-full bg-primary transition-opacity',
            active ? 'opacity-100' : 'opacity-0',
          )}
        />
        <Icon className="h-5 w-5" aria-hidden />
        <span>{label}</span>
      </Link>
    </li>
  );
}
